import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import {
  Button,
  chakra,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent, ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast
} from "@chakra-ui/react";
import { FormInput, FormMultiSelect, FormSelect } from "../../../../components/Forms";
import { showErrorToast, sortAlphabeticallyByAttribute, VALIDATION_PATTERNS } from "../../../../utils";
import * as React from "react";
import { useGetAllSuppliers } from "../../../../hooks/query";
import { hideAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";

function SupplierUserModal({ modalData, isOpen, onClose, onSubmit, roles }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierManagement.supplierUsers.modal"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control
  } = useForm({
    defaultValues: modalData
  });

  const {
    data: suppliers = [],
    isLoading: isLoadingSuppliers
  } = useGetAllSuppliers({
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const onModalClose = () => {
    onClose();
  };

  const prepareSubmitData = data => {
    const mappedData = {
      ...data,
      roleIds: data.roles.map(el => el.value)
    };
    delete mappedData["roles"];
    onSubmit(modalData?.id, mappedData);
  };

  useEffect(() => {
    reset(modalData);
  }, [reset, modalData, isOpen]);

  return !isLoadingSuppliers && isOpen ? <Modal isOpen={isOpen} onClose={onModalClose} size="md">
    <ModalOverlay />
    <ModalContent>
      <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
        <ModalHeader>{modalData ? t("updateSupplierHeader") : t("createSupplierHeader")}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex direction="column" gap="2" marginBottom={10}>
            <FormSelect
              label={`${t("labels.supplier")} *`}
              placeholder={" "}
              errors={errors}
              {...register("supplierId", {
                required: globalTranslation("entityRequired", { entityName: t("labels.supplier") })
              })}
              maxInlineSize={"var(--chakra-sizes-full)"}
            >
              {suppliers
                .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "name"))
                .map(supplier => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)
              }
            </FormSelect>
            {!modalData && <FormInput
              label={`${t("labels.email")} *`}
              type="email"
              errors={errors}
              {...register("username",
                {
                  required: !modalData ? globalTranslation("entityRequired", { entityName: t("labels.email") }) : false,
                  pattern: VALIDATION_PATTERNS.EMAIL_ADDRESS
                })}
              maxInlineSize={"var(--chakra-sizes-full)"}
            />}
            <FormInput
              label={`${t("labels.firstName")} *`}
              errors={errors}
              {...register("firstName", {
                required: globalTranslation("entityRequired", { entityName: t("labels.firstName") })
              })}
              maxInlineSize={"var(--chakra-sizes-full)"}
            />
            <FormInput
              label={`${t("labels.lastName")} *`}
              errors={errors}
              {...register("lastName", {
                required: globalTranslation("entityRequired", { entityName: t("labels.lastName") })
              })}
              maxInlineSize={"var(--chakra-sizes-full)"}
            />
            <FormMultiSelect
              control={control}
              name="roles"
              id="roles"
              options={roles?.map(roleObj => {
                return {
                  label: globalTranslation(`supplierManagement.supplierUsers.roles.${roleObj?.role}`, {
                    defaultValue: roleObj?.role
                  }),
                  value: roleObj?.id
                };
              }) ?? []}
              placeholder={" "}
              label={`${t("labels.userRole")} *`}
              rules={{ required: globalTranslation("entityRequired", { entityName: t("labels.userRole") }) }}
              errors={errors}
            />
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onModalClose}>
            {t("buttons.close")}
          </Button>
          <Button colorScheme="customScheme" type="submit">
            {t("buttons.save")}
          </Button>
        </ModalFooter>
      </chakra.form>
    </ModalContent>
  </Modal> : null;
}

export default SupplierUserModal;